import chroma from "chroma-js";
import _ from "lodash";
import { observer } from "mobx-react-lite";
import React, { useContext } from "react";
import { createGlobalStyle } from "styled-components";
import { AppType } from "../WSUtils/AppType";
import { UserInfoContext } from "../WSUtils/UserInfoContext";
import { AppStyleProps, getThemeSettingValue } from "./getThemeSettingValue";

export const AppGlobalStyles = observer(() => {
  const userInfo = useContext(UserInfoContext);

  return (
    <_AppGlobalStyles
      fontSize={userInfo.settings.fontSize}
      fontFamily={userInfo.settings.fontFamily}
      backgroundColor={userInfo.settings.backgroundColor}
      primaryColor={userInfo.settings.primaryColor}
      themeName={userInfo.settings.theme}
      appType={userInfo.appType}
      cssUrl={userInfo.settings.cssUrl}
    />
  );
});

export const _AppGlobalStyles = createGlobalStyle<AppStyleProps>`

  :root:root {

    --standard-cvp-width: 1032px;

    ${(p) => {
      let primaryColor = p.primaryColor;
      if (!primaryColor) primaryColor = p.theme.primaryColor;

      const cssVariable = primaryColor.match(/var\(--([a-z-0-9,\s]+)\)/);
      if (cssVariable) {
        var asKebabCase = _.kebabCase(cssVariable[1] || "");
        primaryColor = asKebabCase ? p.theme[asKebabCase] : null;
      }

      if (!primaryColor || !chroma.valid(primaryColor)) {
        return;
      }

      const _remap: { [key: string]: string } = {};

      Object.entries(p.theme.replaceableColors).forEach(([key, replacable]) => {
        if (replacable.type === "primary") {
          // use specified primary but with original alpha
          const c = chroma(primaryColor).alpha(chroma(replacable.rgba).alpha());

          _remap[key] = `rgba(${c.rgba()})`;

          return;
        }

        // brighten/darken specified primary by approximately the same amount as the original colors
        let color = chroma(primaryColor);
        const T = replacable.transformationFromPrimary;

        if (T.type === "darken") color = chroma.mix(color, "#000", T.arg);
        if (T.type === "brighten") color = chroma.mix(color, "#fff", T.arg);

        _remap[key] = `rgba(${color.rgba()})`;
      });

      return Object.entries(_remap)
        .map(([key, val]) => `--${key}: ${val};\n`)
        .join("");
    }};

  }

   

  /* base start */

  html {
    ${(p) => {
      if ([AppType.cvWebtempNo, AppType.iframe].includes(p.appType)) {
        return getThemeSettingValue(p, "fontSize");
      }

      return "";
    }};
  }

  #cvpreactroot {
    ${(p) => getThemeSettingValue(p, "backgroundColor")};
  }

  body.cvp-body {
    margin: 0;
    padding: 0;

    &::before {
      // prevent childrens margins from moving body
      // (moved body caused iframe resizer to measure wrong)
      clear: both;
      content: "";
      display: table;
    }
  }

  .p-component {
    box-sizing: border-box;
     
    * {
      box-sizing: border-box; 
    }

    &.p-component-overlay, &.cvp-app-container {
      line-height: normal;
    }
  }



  .cvp-flex-align-center {
    display: flex;
    align-items: center,
  }


  /* base end */

  /* Typography start*/

  .cvp-h1 {
    display: block;
    font-size: 2em;
    margin-block-start: 0.67em;
    margin-block-end: 0.67em;
    margin-inline-start: 0px;
    margin-inline-end: 0px;
    font-weight: bold;
  }

  .cvp-h2 {
    display: block;
    font-size: 1.5em;
    margin-block-start: 0.83em;
    margin-block-end: 0.83em;
    margin-inline-start: 0px;
    margin-inline-end: 0px;
    font-weight: bold;
  }

  .cvp-h3 {
    display: block;
    font-size: 1.17em;
    margin-block-start: 1em;
    margin-block-end: 1em;
    margin-inline-start: 0px;
    margin-inline-end: 0px;
    font-weight: bold;
  }

  .cvp-h4 {
    display: block;
    margin-block-start: 1.33em;
    margin-block-end: 1.33em;
    margin-inline-start: 0px;
    margin-inline-end: 0px;
    font-weight: bold;
  }

  .cvp-h5 {
    display: block;
    font-size: 0.83em;
    margin-block-start: 1.67em;
    margin-block-end: 1.67em;
    margin-inline-start: 0px;
    margin-inline-end: 0px;
    font-weight: bold;
  }

  .cvp-h6 {
    display: block;
    font-size: 0.67em;
    margin-block-start: 2.33em;
    margin-block-end: 2.33em;
    margin-inline-start: 0px;
    margin-inline-end: 0px;
    font-weight: bold;
  }

   /* Typography end*/



  .cvp-awaiting-translation {
    visibility: hidden;
  }

  .cvp-confirmation-dialog {
    min-width: min(100%, 360px);
  }

  .cvp-confirmation-dialog-no-icon {
    .p-confirm-dialog-message.p-confirm-dialog-message.p-confirm-dialog-message {
      margin-left: 0;
    }
  }

  .cv-multiselect-grow {
    .p-multiselect-label {
      display: flex;
      flex-wrap: wrap;
      padding-bottom: 0;

      .p-multiselect-token {
        margin-bottom: 0.25rem;
      }
    }
  }

  .cv-treeselect-empty {
    padding: 0.25rem;
  }

  .cv-treeselect-empty-no-message {
    visibility: hidden;
  }

  .cv-treeselect-grow {
    .p-treeselect-label:not(.p-placeholder) {
      display: flex;
      flex-wrap: wrap;
      padding-bottom: 0;

      .p-treeselect-token {
        border-radius: 3px;
        width: 100%;

        &:not(:last-child) {
          margin-bottom: 0.25rem;
        }
      }
    }
    
    .cve-treeselect-value-remove-button {
      margin-left: auto;
    }
  }

  .cve-treeselect-value-remove-button {
    cursor: pointer;
  }

  .p-button-icon-only {
    .p-button-label {
      display: none;
    }
  }

  .cvp-checkbox {
    label {
      cursor: pointer;
    }
  }



`;

export const UserProvidedStyle = createGlobalStyle<{
  css: string;
}>`
${(p) => p.css ?? ""}; 
`;
